import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, MenuItem, Paper, TextField, Typography, makeStyles } from '@material-ui/core'
import { Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { nanoid } from '@reduxjs/toolkit'
import { selectCredentials } from '@views/loginView/state/loginSlice'
import { userProfile } from '@views/profile/state/profileSlice'
import { DailyChart, DistributionChart, MonthlyAverageChart } from '../agentMetrics/components/Charts'
import { clearExecutiveMetrics, loadExecutiveMetrics, selectExecutiveMetrics } from './state/executiveMetricsSlice'
import { bogotaPeriod, canViewExecutiveMetrics } from './utils'
import StarCasesChart from './components/StarCasesChart'

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: 1500,
    margin: '84px auto 0',
    padding: theme.spacing(3),
    minHeight: 'calc(100vh - 84px)',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
      marginTop: 72
    }
  },
  heading: { fontWeight: 700 },
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    margin: theme.spacing(2, 0)
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0, 1fr)' }
  },
  status: { padding: theme.spacing(6), textAlign: 'center' },
  live: { color: '#66707a' }
}))

const ExecutiveMetricsContent: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const profile = useSelector(userProfile)
  const state = useSelector(selectExecutiveMetrics)
  const { userId, cocap } = useSelector(selectCredentials)
  const requestId = useRef('')
  const initialPeriod = bogotaPeriod()
  const [period, setPeriod] = useState(initialPeriod)
  const [year, setYear] = useState(Number(initialPeriod.slice(0, 4)))
  const allowed = canViewExecutiveMetrics(profile.role)

  useEffect(() => {
    if (allowed) {
      requestId.current = nanoid()
      dispatch(loadExecutiveMetrics({ period, year, requestId: requestId.current }))
    }
    return () => {
      dispatch(clearExecutiveMetrics())
    }
  }, [allowed, dispatch, period, year, userId, cocap])

  useEffect(() => {
    if (!state.data) return
    if (period < state.data.range.minimumPeriod) setPeriod(state.data.range.minimumPeriod)
    if (period > state.data.range.maximumPeriod) setPeriod(state.data.range.maximumPeriod)
  }, [period, state.data])

  if (!allowed) return <Redirect to="/" />
  const years = state.data
    ? Array.from(
        {
          length: state.data.range.maximumYear - state.data.range.minimumYear + 1
        },
        (_, index) => state.data!.range.minimumYear + index
      )
    : [year]
  const retry = () => {
    requestId.current = nanoid()
    dispatch(loadExecutiveMetrics({ period, year, requestId: requestId.current }))
  }

  return (
    <Box className={classes.root}>
      <Typography className={classes.heading} variant="h4">
        Métricas
      </Typography>
      <Typography className={classes.live} variant="body2">
        Datos actuales de los casos ahorradores asignados a ti. Las reasignaciones pueden cambiar los resultados
        históricos.
      </Typography>
      <Box className={classes.filters} aria-label="Filtros de métricas">
        <TextField
          id="executive-metrics-month"
          label="Mes"
          type="month"
          value={period}
          inputProps={{
            min: state.data?.range.minimumPeriod,
            max: state.data?.range.maximumPeriod
          }}
          onChange={event => {
            const value = event.target.value
            setPeriod(value)
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          id="executive-metrics-year"
          select
          label="Año"
          value={year}
          onChange={event => setYear(Number(event.target.value))}
        >
          {years.map(value => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {state.requestKey !== requestId.current ||
      !requestId.current ||
      state.loading ||
      (state.data && (state.data.period !== period || state.data.year !== year)) ? (
        <Paper className={classes.status}>
          <CircularProgress aria-label="Cargando métricas" />
        </Paper>
      ) : state.error ? (
        <Paper className={classes.status}>
          <Typography gutterBottom>No fue posible cargar las métricas.</Typography>
          <Button color="primary" variant="contained" onClick={retry}>
            Reintentar
          </Button>
        </Paper>
      ) : state.data ? (
        <>
          {state.data.selectedMonthTotal === 0 && (
            <Paper className={classes.status}>No hay casos registrados en el mes seleccionado.</Paper>
          )}
          <Box className={classes.grid}>
            <DailyChart data={state.data.dailyRegistrations} />
            <MonthlyAverageChart data={state.data.monthlyAverages} />
            <DistributionChart
              title="Casos por banco"
              description="Distribución de los casos del mes según banco."
              data={state.data.banks}
            />
            <DistributionChart
              title="Casos por ciudad"
              description="Top 10 de ciudades normalizadas; las restantes se agrupan como OTRAS."
              data={state.data.cities}
            />
            <DistributionChart
              title="Porcentaje de efectividad"
              description="Los estados actuales Negado y Devuelto tienen prioridad sobre el completo en oficina. Todos los casos permanecen incluidos en el total mensual."
              summary={`Total de casos del mes: ${state.data.selectedMonthTotal}`}
              data={
                state.data.selectedMonthTotal
                  ? [
                      {
                        label: 'Con completo en oficina',
                        count: state.data.effectiveness.completed
                      },
                      {
                        label: 'Sin completo en oficina',
                        count: state.data.effectiveness.pending
                      },
                      {
                        label: 'Devueltos',
                        count: state.data.effectiveness.returned ?? 0
                      },
                      {
                        label: 'Negados',
                        count: state.data.effectiveness.rejected ?? 0
                      }
                    ]
                  : []
              }
            />
            <DistributionChart
              title="CRM / No CRM"
              description="Origen CRM de los casos registrados en el mes."
              data={
                state.data.selectedMonthTotal
                  ? [
                      { label: 'CRM', count: state.data.crm.crm },
                      { label: 'No CRM', count: state.data.crm.nonCrm }
                    ]
                  : []
              }
            />
            <DistributionChart
              title="Directos / Referidos"
              description="Casos del mes registrados directamente o por un referidor."
              data={
                state.data.selectedMonthTotal
                  ? [
                      { label: 'Directos', count: state.data.origin.direct },
                      { label: 'Referidos', count: state.data.origin.referred }
                    ]
                  : []
              }
            />
            <StarCasesChart
              data={
                state.data.starCases || {
                  qualified: 0,
                  other: state.data.selectedMonthTotal,
                  total: state.data.selectedMonthTotal,
                  percentage: 0
                }
              }
            />
          </Box>
        </>
      ) : null}
    </Box>
  )
}

const ExecutiveMetricsView: React.FC = () => {
  const { userId, cocap } = useSelector(selectCredentials)
  return <ExecutiveMetricsContent key={`${userId}:${cocap}`} />
}

export default ExecutiveMetricsView
